"use client";

import React, { FunctionComponent, useEffect, useRef, useState } from "react";
import { animated, useSpring } from "@react-spring/web";

import { IHubspotForm, IHubspotFormFields } from "@/types/generated/contentful";
import { IForm, IFieldGroup, IField, IFormState } from "@/types/hubspot";
import { validateField } from "@/helpers/formValidation";
import Disclaimer from "./disclaimer";
import FieldGroup from "./fieldGroup";
import FormError from "./formError";
import FormSubmit from "./formSubmit";
import FormRichText from "./formRichText";
import { SendGTMEvent } from "@/services/gtmEvents";
import { GTMEvent } from "@/types/gtm";
import { FormSkeleton } from "./formSkeleton";

type Props = {
  content: IHubspotForm;
  foreground?: string;
};

const HubspotForm: FunctionComponent<Props> = ({ content }) => {
  const { formId, successTitle, successBody, background, foreground } =
    content.fields as IHubspotFormFields;

  const formRef = useRef<HTMLDivElement>(null);
  const [isLoading, setLoading] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);
  const [isComplete, setCompleted] = useState(false);
  const [hsForm, setForm] = useState<IForm | undefined | null>(undefined);
  const [fields, setFields] = useState<IFormState>({});
  const [error, setError] = useState("");
  const { display, height, opacity, scale } = useSpring({
    display: isComplete ? "" : "hidden",
    height: isComplete ? "auto" : "0px",
    opacity: isComplete ? 1 : 0,
    scale: isComplete ? 1 : 0,
  });

  useEffect(() => {
    setLoading(true);
    fetch(`/api/form/${formId}`)
      .then((res) => res.json())
      .then((data) => {
        let fieldGroups = {};
        setFields(
          data.fieldGroups.reduce((object: any, item: IFieldGroup) => {
            let fields = {};
            const field = item.fields.reduce((object, item: IField) => {
              return {
                ...object,
                [item.name]: {
                  ...item,
                  value: item.defaultValue ? item.defaultValue : "",
                  error: "",
                },
              };
            }, fields);
            return {
              ...object,
              ...field,
            };
          }, fieldGroups),
        );
        setForm(data as IForm);
        setLoading(false);
      })
      .catch(() => {
        setForm(null);
      });
  }, [formId]);

  const handleFieldUpdate = (field: IField) => {
    const error = validateField(field);

    setFields({
      ...fields,
      [field.name]: {
        ...field,
        error: error,
      },
    });
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    let hasError = false;
    let formValidation = {};
    let submission = {
      fields: {},
      context: {
        pageUri: window.location.href,
        pageName: document.title,
      },
    };

    for (let field in fields as IFormState) {
      let error = validateField(fields[field]);
      hasError = error != "" ? true : hasError;
      formValidation = {
        ...formValidation,
        [field]: {
          ...fields[field],
          error: error,
        },
      };
      submission.fields = {
        ...submission.fields,
        [field]: fields[field].value,
      };
    }

    if (hasError) {
      setFields(formValidation);
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      setSubmitting(true);
      fetch(`/api/form/${formId}`, {
        method: "post",
        headers: new Headers({ "content-type": "application/json" }),
        body: JSON.stringify(submission),
      })
        .then((res) => {
          if (!res.ok) throw res;
          return res.json();
        })
        .then(() => {
          setSubmitting(false);
          setCompleted(true);

          SendGTMEvent(GTMEvent.FORM_SUBMITTED, {
            id: formId,
          });
        })
        .catch((error) => {
          setSubmitting(false);
          error.json().then((body: any) => {
            setError(body.error);
            formRef.current?.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          });
        });
    }
  };

  const formComponent = () => {
    if (isLoading) return <></>;
    else if (!isLoading && hsForm === null)
      return <p>Something went wrong, please reload</p>;
    else if (hsForm) {
      return (
        <form
          className="mx-auto md:max-w-xl"
          onSubmit={handleSubmit}
          noValidate
        >
          <FormError message={error} />
          <fieldset disabled={isSubmitting}>
            {hsForm.fieldGroups.map((group, index) => {
              return (
                <FieldGroup
                  key={`group-${index}`}
                  fieldGroup={group}
                  fields={fields}
                  callback={handleFieldUpdate}
                />
              );
            })}
          </fieldset>
          <FormSubmit form={hsForm} isSubmitting={isSubmitting} />
          <Disclaimer />
        </form>
      );
    }
  };

  if (isLoading)
    return (
      <div
        className="mx-auto flex flex-row md:max-w-xl"
        style={{
          backgroundColor: background || "transparent",
          ...(foreground && { color: foreground }),
        }}
      >
        <FormSkeleton />
      </div>
    );

  return (
    <div
      className="fade-in flex h-full flex-col items-center justify-center rounded-xl"
      style={{
        backgroundColor: background || "transparent",
        ...(foreground && { color: foreground }),
      }}
    >
      <animated.div
        className="flex h-full flex-col justify-start"
        style={{ display: isComplete ? "none" : "" }}
      >
        <div
          className={`overflow-hidden ${!isComplete && "p-4"}`}
          ref={formRef}
        >
          {formComponent()}
        </div>
      </animated.div>
      <animated.div
        className="flex h-full w-full flex-col items-center justify-center"
        style={{
          display,
          height,
          opacity,
          scale,
        }}
      >
        <div className={`h-full w-full p-4 ${ background ? "lg:px-4": "lg:px-0"}`}>
          <div className="w-full text-center text-2xl font-bold">
            {successTitle}
          </div>
          {successBody && (
            <div className="text-center">
              <FormRichText content={successBody} />
            </div>
          )}
        </div>
      </animated.div>
    </div>
  );
};

export default HubspotForm;
